import React, { useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { MdAdminPanelSettings, MdArrowForward } from 'react-icons/md'

export default function AdminLogin() {
  const navigate = useNavigate()
  const url = useSelector(state => state.url)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const login = async (event) => {
    event.preventDefault()

    if (!username.trim() || !password) {
      setError('Enter your username and password.')
      return
    }

    try {
      setLoading(true)
      setError('')

      const formData = new FormData()
      formData.append('username', username)
      formData.append('password', password)

      const response = await axios.post(
        `${url}/admin/login`,
        formData
      )

      const data = response.data

      if (data.status_code === 200 && data.access_token) {
        localStorage.setItem('access_token', data.access_token)
        navigate('/dashboard', { replace: true })
        return
      }

      setError(data.error || 'Unable to sign in.')

    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        'Unable to sign in right now.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F7F9FC] font-[Satoshi] lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      <section className="relative hidden overflow-hidden bg-[#101828] px-12 py-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-green-500/20 blur-3xl" />
        <div className="absolute -bottom-20 left-10 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-500 text-xl font-black shadow-lg shadow-green-950/20">
            R
          </div>
          <div>
            <p className="text-xl font-bold tracking-tight">RentHub</p>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Admin Console</p>
          </div>
        </div>

        <div className="relative max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-semibold text-green-300">
            <MdAdminPanelSettings className="text-lg" />
            Operations workspace
          </span>

          <h1 className="mt-6 text-5xl font-bold leading-[1.08] tracking-tight">
            Run RentHub operations from one place.
          </h1>

          <p className="mt-5 max-w-lg text-lg leading-8 text-slate-300">
            Review applications, manage landlord disbursements, monitor repayments and keep overdue accounts visible.
          </p>
        </div>

        <p className="relative text-sm text-slate-500">
          RentHub is a product of Rivmev Services.
        </p>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-600 text-xl font-black text-white">
              R
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">RentHub</p>
              <p className="text-xs uppercase tracking-[0.18em] text-gray-400">Admin Console</p>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-8">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-600">
                <MdAdminPanelSettings className="text-2xl" />
              </div>

              <p className="text-sm font-semibold text-green-600">WELCOME BACK</p>
              <h2 className="mt-1 text-3xl font-bold tracking-tight text-gray-900">Admin sign in</h2>
              <p className="mt-2 text-sm leading-6 text-gray-500">
                Use your RentHub administrator credentials to continue.
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                {error}
              </div>
            )}

            <form onSubmit={login} className="space-y-5">
              <div>
                <label htmlFor="username" className="mb-2 block text-sm font-semibold text-gray-700">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Enter admin username"
                  className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-green-500 focus:ring-4 focus:ring-green-100"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-semibold text-gray-700">
                  Password
                </label>

                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter password"
                    className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 pr-12 text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-green-500 focus:ring-4 focus:ring-green-100"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(value => !value)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-gray-700"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-5 font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
              >
                {loading ? 'Signing in...' : 'Sign in to Admin'}
                {!loading && <MdArrowForward className="text-xl" />}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center text-xs leading-5 text-gray-400">
            Authorized RentHub personnel only.
          </p>
        </div>
      </main>
    </div>
  )
}
